// Thank-You page layout data — follows the same COMPONENT_REGISTRY pattern.
// Single visual design (the former "Card Default" layout); admin edits are
// organized by payment method (Card/Transfer/Cash/Tabby) instead of a
// visual-layout picker. All dynamic order content (orderId, payment method
// selection) is still driven by the page itself — colors, every page text
// string, and per-payment-method instructions are admin-configurable.

// Seed values below are copied verbatim from the live client's i18n JSON
// (meelz-client-v2/src/messages/{en,ru,ar}.json) at the time this field was
// introduced — see plan-v4.md Step 0/1 for the source line numbers. Seeding
// all 3 languages (not just English) means no language regresses to blank
// on first load after this ships.
const THANK_YOU_DEFAULT_PROPS = {
    primaryColor:       "#573121",
    backgroundColor:    "#F3F4F6",
    textColor:          "#1a1a1a",
    titleTextColor:     "#ffffff",
    ctaButtonTextColor: "#ffffff",
    title: {
        en: "Thank you for your order",
        ru: "Спасибо за заказ",
        ar: "شكراً لطلبك",
    },
    changeOrderInstructions: {
        en: "If you want to make changes or replace dishes, you can do so from your dashboard in order history.",
        ru: "Чтобы изменить заказ или заменить блюда, сделайте это в панели управления в истории заказов.",
        ar: "إذا كنت ترغب في إجراء تغييرات أو استبدال الأطباق، يمكنك ذلك من لوحة التحكم في سجل الطلبات.",
    },
    customNote: "",
    // Fixed 4-method object — see PaymentMethodTextsEditor. Tabby is seeded
    // from `paymentSuccessfulDefault` (the closest existing equivalent),
    // since Tabby has never had its own dedicated instruction text.
    paymentMethodTexts: {
        Card: {
            instructions: {
                en: "To complete your order, please click the 'Pay Now' button below to process your payment securely.",
                ru: "Чтобы завершить заказ, нажмите кнопку «Оплатить сейчас» для безопасной оплаты.",
                ar: "لإكمال طلبك، انقر على زر \"ادفع الآن\" أدناه لمعالجة الدفع بشكل آمن.",
            },
        },
        Transfer: {
            instructions: {
                en: "Please transfer money to the account number: 9736556203, IBAN: AE520860000009736556203, BIC: WIOBAEADXXX, Beneficiary name: RPT RESTAURANT L.L.C and send the order number and payment receipt.",
                ru: "Переведите сумму на счёт: 9736556203, IBAN: AE520860000009736556203, BIC: WIOBAEADXXX, получатель: RPT RESTAURANT L.L.C и отправьте номер заказа и чек об оплате.",
                ar: "يرجى تحويل المبلغ إلى رقم الحساب: 9736556203، الآيبان: AE520860000009736556203، BIC: WIOBAEADXXX، اسم المستفيد: RPT RESTAURANT L.L.C وإرسال رقم الطلب وإيصال الدفع.",
            },
        },
        Cash: {
            instructions: {
                en: "Please pay to your courier on the first order.",
                ru: "Оплатите курьеру при первом заказе.",
                ar: "يرجى الدفع للمندوب عند أول طلب.",
            },
        },
        Tabby: {
            instructions: {
                en: "Payment was successful, and your order has been processed.",
                ru: "Оплата прошла успешно, заказ обработан.",
                ar: "تمت عملية الدفع بنجاح، وتمت معالجة طلبك.",
            },
        },
    },
};

// CK: real, brand-owned entry (plan.md Context section's brand-content-
// isolation audit / Step 6.0) — the pre-existing `default` key above is
// genuinely meelz's own real copy (sourced from meelz's i18n JSON, per this
// file's own header comment), not a brand-agnostic placeholder every brand
// shared identically — renamed to `meelz` below so it's a real, correctly-
// named brand key like hero/navbar/footer's own `meelz`/`e4f`/`ck` keys,
// and CK gets its own real content instead of seeing/publishing meelz's
// text as if it were CK's own. Content extracted verbatim from
// ck-client-ck's own lib/dictionary.ts (English only — CK has no ru/ar
// locale files, only en/ru/ge, and ge isn't part of this field's existing
// {en,ru,ar} shape — leaving ru/ar absent rather than duplicating English
// into them). Confirmed via ck-client-ck's lib/order.ts: PaymentMethod is
// `"Card" | "Transfer" | "Cash" | null` — no Tabby variant exists in CK at
// all, so CK's own Tabby entry is omitted entirely (not seeded with a
// placeholder), unlike meelz's, which does support it.
// primaryColor/backgroundColor are NOT set here — this component is a
// HERO_FALLBACK_COMPONENTS member, always sourced from hero's own real
// per-brand colors (see getHeroFallbackColors()), independent of which
// layout key is selected; any value set here would be silently overridden
// at buildDefaultEntry() time and would only mislead a future reader.
const THANK_YOU_CK_DEFAULT_PROPS = {
    titleTextColor:     "#ffffff",
    ctaButtonTextColor: "#ffffff",
    title: {
        en: "Thank you for your order",
    },
    changeOrderInstructions: {
        en: "If you want to make changes and add or replace dishes, log in to your personal account in the order history",
    },
    customNote: "",
    paymentMethodTexts: {
        Card: {
            instructions: {
                en: "To complete your order, please click the 'Pay Now' button below to process your payment securely.",
            },
        },
        Transfer: {
            instructions: {
                en: "Please transfer money to the account GE94BG0000000594138377, Beneficiary name: I/E Anna Utkina and send the order number and payment receipt.",
            },
        },
        Cash: {
            instructions: {
                en: "Please pay to your courier on the first order.",
            },
        },
    },
};

// E4F's own real content — extracted verbatim from ck-client-e4f's
// lib/dictionary.ts (changeOrderInstructions/paymentInstructionCard/
// Transfer/Cash keys). Same single-"en"-key shape as THANK_YOU_CK_DEFAULT_PROPS
// (this repo's translatable-field resolver falls back to "en" for missing
// languages) and the same omission of primaryColor/backgroundColor — the
// e4f client's own thank-you page already has its own real brand-red
// (#b11212) JS fallback for when no override is published, exactly like
// CK's page does for its own colors.
//
// Was previously MISSING entirely — DEFAULT_LAYOUT_BY_BRAND already maps
// E4F -> "e4f" for this component, but with no "e4f" key here,
// buildDefaultEntry's fallback (`Object.keys(entry.layouts)[0]`) silently
// picked "meelz" as the layout id an E4F admin session edited AND
// published under — meaning every E4F thankYou publish was actually being
// saved to the "meelz" byLayout entry, never anywhere ck-client-e4f's own
// lib/thankYouConfig.ts (hardcoded `byLayout["e4f"]` lookup, matching the
// per-brand-key convention CK/MEELZ already use) could ever read it back.
// Confirmed bug: any E4F admin edit to payment instructions never reached
// the live client, regardless of what was published.
const THANK_YOU_E4F_DEFAULT_PROPS = {
    titleTextColor:     "#ffffff",
    ctaButtonTextColor: "#ffffff",
    title: {
        en: "Thank you for your order",
    },
    changeOrderInstructions: {
        en: "If you want to make changes and add or replace dishes, log in to your personal account in the order history",
    },
    customNote: "",
    paymentMethodTexts: {
        Card: {
            instructions: {
                en: "To complete your order, please click the 'Pay Now' button below to process your payment securely.",
            },
        },
        Transfer: {
            instructions: {
                en: "Please transfer money to the account GE94BG0000000594138377, Beneficiary name: I/E Anna Utkina and send the order number and payment receipt.",
            },
        },
        Cash: {
            instructions: {
                en: "Please pay to your courier on the first order.",
            },
        },
    },
};

export const THANK_YOU_LAYOUTS = {
    meelz: { label: "Thank-You Page (MEELZ)", defaultProps: THANK_YOU_DEFAULT_PROPS },
    ck:    { label: "Thank-You Page (CK)",    defaultProps: THANK_YOU_CK_DEFAULT_PROPS },
    e4f:   { label: "Thank-You Page (E4F)",   defaultProps: THANK_YOU_E4F_DEFAULT_PROPS },
};

export const THANK_YOU_PAYMENT_METHODS = ["Card", "Transfer", "Cash", "Tabby"];

// payNowLabel/goToDashboardLabel intentionally excluded — button TEXT is not
// admin-editable on this page (per explicit product decision), unlike every
// other field here (colors, title, instructions, custom note). The client
// page falls back to its i18n translation keys (t("payNow")/t("goToDashboard"))
// for both buttons now that no builder override can be published for them.
export const THANK_YOU_SCHEMA = [
    { key: "primaryColor",             label: "Primary color",              type: "color" },
    { key: "backgroundColor",          label: "Page background",            type: "color" },
    { key: "textColor",                label: "Text color",                 type: "color" },
    { key: "titleTextColor",           label: "Heading text color",         type: "color" },
    { key: "ctaButtonTextColor",       label: "Pay Now button text color",  type: "color" },
    { key: "title",                    label: "Page title",                 type: "text", translatable: true, wrapControl: true },
    { key: "changeOrderInstructions",  label: "Change-order instructions",  type: "text", translatable: true, wrapControl: true },
    { key: "customNote",               label: "Custom note (opt.)",         type: "text", translatable: true, wrapControl: true },
    { key: "paymentMethodTexts",       label: "Payment-method instructions", type: "paymentMethodTexts" },
];
